import React from 'react';
import {
  Select,
  SelectContent,
  SelectField,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import { Eye, EyeOff } from 'lucide-react';
import loginBanner from '@/assets/login_page_banner.png';
import logo from '@/assets/logo.svg';

/**
 * Project info from API
 */
export interface ProjectOption {
  reference: string;
  display: string;
}

export type LoginViewProps = {
  branding: { appName: string; appFullName?: string; appTagline?: string; version?: string };
  labels: { title: string; subtitle?: string };
  state: { isLoading: boolean };
  /** Available projects when user has multiple - shown only when provided */
  availableProjects?: ProjectOption[];
  onSubmit: (data: { email: string; password: string; projectReference?: string }) => void;
};

export function LoginView({ labels, state, availableProjects, onSubmit }: LoginViewProps) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [selectedProject, setSelectedProject] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  // Check if we need to show hospital selection (user has multiple projects)
  const showHospitalSelection = availableProjects && availableProjects.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (state.isLoading) return;

    // Validate required fields
    if (!email.trim() || !password) return;

    // If hospital selection is shown, require a selection
    if (showHospitalSelection && !selectedProject) return;

    onSubmit({
      email,
      password,
      projectReference: selectedProject || undefined,
    });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  // Determine if submit is disabled
  const isSubmitDisabled =
    state.isLoading || !email.trim() || !password || (showHospitalSelection && !selectedProject);

  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-gradient-to-br from-[#e8ebf8] to-[#f5f6f9]">
      {/* Left Panel - Login Form */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center px-4 lg:w-1/2">
        {/* Logo at top */}
        <div className="absolute left-8 top-8">
          <img src={logo} alt="Logo" style={{ height: 'calc(32px * var(--app-scale))' }} />
        </div>

        {/* Main Content */}
        <div className="w-full max-w-[calc(360px*var(--app-scale))]">
          {/* Title */}
          <h1 className="mb-3 text-center font-serif text-[calc(36px*var(--app-scale))] font-normal leading-[calc(48px*var(--app-scale))] tracking-tight text-[#253057]">
            {labels.title || 'Welcome to HMIS'}
          </h1>

          {/* Subtitle */}
          <p
            className="text-center font-hanken text-[calc(15px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] tracking-label-1 text-[#526ec5]"
            style={{ marginBottom: 'calc(59px * var(--app-scale))' }}
          >
            {labels.subtitle || 'Sign In to get started'}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Email Input */}
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder="Email address"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={handleEmailChange}
                disabled={state.isLoading}
                className="w-full rounded-full border-none bg-white px-4 py-3 font-hanken text-[calc(18px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-gray-700 placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#526ec5]/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={handlePasswordChange}
                disabled={state.isLoading}
                className="w-full rounded-full border-none bg-white px-4 py-3 pr-12 font-hanken text-[calc(18px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-gray-700 placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#526ec5]/20 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#979698] hover:text-gray-700 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? (
                  <Eye className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))]" />
                ) : (
                  <EyeOff className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))]" />
                )}
              </button>
            </div>

            {/* Hospital selector - shows only when user has multiple projects */}
            {showHospitalSelection && (
              <div className="relative">
                <SelectField>
                  <Select
                    value={selectedProject}
                    onValueChange={setSelectedProject}
                    disabled={state.isLoading}
                  >
                    <SelectTrigger className="w-full rounded-full border-none bg-white px-4 py-3 font-hanken text-[calc(18px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-gray-700">
                      <SelectValue placeholder="Select Hospital" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProjects.map((project) => (
                        <SelectItem key={project.reference} value={project.reference}>
                          {project.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectField>
              </div>
            )}

            {/* Forgot Password Link */}
            <button
              type="button"
              className="w-full text-center font-hanken text-[calc(18px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#5d7cdd] hover:text-[#526ec5] focus:outline-none"
            >
              Forgot password?
            </button>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="relative w-full overflow-hidden rounded-full border border-[#bcc0d7] bg-[#f5f6f9] font-sans text-[calc(16px*var(--app-scale))] font-semibold text-black shadow-[inset_0px_1px_1px_0px_#d1dbfd,inset_0px_-2px_3px_0px_#f8f9fb] transition-all hover:bg-[#eaebf3] focus:outline-none focus:ring-2 focus:ring-[#526ec5]/20 disabled:cursor-not-allowed disabled:text-[#969aac] disabled:opacity-50"
              style={{ height: 'calc(44px * var(--app-scale))' }}
            >
              <div className="absolute inset-0 flex items-center justify-center bg-[rgba(208,219,252,0.4)] blur-[2px]" />
              <span className="relative">
                {state.isLoading ? 'Signing in...' : 'Login to experience AI'}
              </span>
            </button>
          </form>
        </div>

        {/* Powered by Footer */}
        <div className="absolute bottom-9 left-1/2 -translate-x-1/2">
          <p className="text-center font-hanken text-[calc(18px*var(--app-scale))] font-bold leading-[calc(24px*var(--app-scale))] tracking-[-0.16px] text-[#4d68b9]">
            Powered by Pranik.AI
          </p>
        </div>
      </div>

      {/* Right Panel - Banner Image */}
      <div className="hidden lg:block lg:w-1/2">
        <div className="relative h-full w-full overflow-hidden bg-gray-100">
          {/* Background Image */}
          <img
            src={loginBanner}
            alt="Healthcare professional consultation"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      </div>
    </div>
  );
}
